import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/client';
import { QuotationData, CargoType, VehicleType, Currency } from '../types';
import { formatCurrency } from '../utils/calculationUtils';
import { generateQuotationPDF } from '../utils/pdfUtils';
import { FileText, MapPin, Trash2, AlertCircle, ShieldAlert, Package, DollarSign, Image } from 'lucide-react';
import VehicleIcon from './VehicleIcon';
import { getCurrencySymbol, convertCurrency } from '../utils/currencyUtils';
import { analyticsService } from '../services/analyticsService';
import useTranslate from '../hooks/useTranslate';
import { useLanguage } from '../contexts/LanguageContext';
import { downloadImage, dataURLToBlob, downloadBlob, createTextImageBlob } from '../utils/imageUtils';

interface QuotationHistoryProps {
  userId: string;
}

const QuotationHistory: React.FC<QuotationHistoryProps> = ({ userId }) => {
  const [quotations, setQuotations] = useState<QuotationData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [expandedQuotation, setExpandedQuotation] = useState<string | null>(null);
  
  // Get translations
  const { t } = useLanguage();
  const { translateVehicleType, translateTrailerType, translateCargoType } = useTranslate();

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const { data: quotations, error } = await supabase
        .from('quotations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      const quotationsList: QuotationData[] = [];
      
      quotations?.forEach(data => {
        quotationsList.push({
          id: data.id,
          userId: data.user_id,
          origin: data.origin,
          destination: data.destination,
          distance: data.distance,
          fuelPrice: data.fuel_price,
          vehicleType: data.vehicle_type,
          trailerType: data.trailer_type,
          totalCost: data.total_cost,
          profitMargin: data.profit_margin,
          profitAmount: data.profit_amount,
          baseCost: data.base_cost,
          cargoType: data.cargo_type,
          riskFactor: data.risk_factor,
          riskAmount: data.risk_amount,
          createdAt: new Date(data.created_at),
          mapImageUrl: data.map_image_url,
          originAddress: data.origin_address,
          destinationAddress: data.destination_address,
          costBreakdown: data.cost_breakdown,
          tollDetails: data.toll_details,
          selectedCurrency: data.selected_currency,
          isMultiSegmentRoute: data.is_multi_segment_route,
          segmentDistances: data.segment_distances,
          segmentDurations: data.segment_durations,
          segmentLoadStatus: data.segment_load_status,
          waypoints: data.waypoints,
          capturedMapImage: data.captured_map_image,
        });
      });
      
      quotationsList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setQuotations(quotationsList);
      
      // Track history view in analytics
      analyticsService.trackCustomEvent('quotation_history_view', {
        quotation_count: quotationsList.length
      });
    } catch (err: any) {
      console.error('Error fetching quotations:', err);
      setError('Error al cargar el historial de cotizaciones. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchQuotations();
  }, [userId]);

  const handleDownloadPDF = async (quotation: QuotationData) => {
    // Ensure tollDetails is properly formatted with dates
    if (quotation.tollDetails && quotation.tollDetails.lastUpdated) {
      // Ensure lastUpdated is a Date object
      if (typeof quotation.tollDetails.lastUpdated === 'object' && 
          'toDate' in quotation.tollDetails.lastUpdated && 
          typeof quotation.tollDetails.lastUpdated.toDate === 'function') {
        quotation.tollDetails.lastUpdated = quotation.tollDetails.lastUpdated.toDate();
      } else if (!(quotation.tollDetails.lastUpdated instanceof Date)) {
        quotation.tollDetails.lastUpdated = new Date();
      }
    }
    
    try {
      // Track PDF download in analytics
      analyticsService.trackQuotationDownloaded(quotation.id || 'unknown');
      
      // Generate PDF with the static map URL
      const imageToUse = quotation.mapImageUrl || undefined;
      const doc = await generateQuotationPDF(quotation, imageToUse, quotation.selectedCurrency);
      doc.save(`cotizacion-${quotation.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  const handleDownloadMapImage = async (quotation: QuotationData) => {
    try {
      const hasMapImage = !!quotation.mapImageUrl;

      if (!hasMapImage) {
        throw new Error(t('quotationForm.errors.mapImageMissing'));
      }

      // Create filename with quotation ID
      const filename = `mapa-ruta-${quotation.id || 'cotizacion'}.jpg`;

      // Use only the static map URL
      if (quotation.mapImageUrl) {
        console.log('Descargando mapa desde URL estática');
        await downloadImage(quotation.mapImageUrl, filename);
        console.log('Descarga exitosa desde URL estática');
      } else {
        // Fallback - create a fallback image
        console.log('Creando imagen de texto de respaldo');
        const fallbackBlob = await createTextImageBlob('No hay imagen de mapa disponible');
        downloadBlob(fallbackBlob, filename);
        console.log('Descarga exitosa usando imagen de respaldo');
      }
      
      // Track the download event
      analyticsService.trackCustomEvent('map_image_downloaded', {
        quotationId: quotation.id || 'history',
        standalone: true
      });
      
    } catch (error) {
      console.error('Error downloading map image:', error);
      setError(t('quotationForm.errors.downloadFailed'));
    }
  };

  const handleDeleteClick = (quotationId: string) => {
    setShowDeleteConfirm(quotationId);
    setDeleteSuccess(false);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleConfirmDelete = async (quotationId: string) => {
    try {
      setDeleteLoading(quotationId);
      setDeleteError(null);
      
      const { error } = await supabase
        .from('quotations')
        .delete()
        .eq('id', quotationId);
      
      if (error) throw error;
      
      setQuotations(prevQuotations => 
        prevQuotations.filter(quotation => quotation.id !== quotationId)
      );
      
      // Track delete in analytics
      analyticsService.trackCustomEvent('quotation_deleted', {
        quotation_id: quotationId
      });
      
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error deleting quotation:', err);
      setDeleteError('Error al eliminar la cotización. Por favor, intenta de nuevo.');
      
      // Track delete error in analytics
      analyticsService.trackCustomEvent('quotation_delete_error', {
        error_message: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setDeleteLoading(null);
      setShowDeleteConfirm(null);
    }
  };
  
  // Toggle expanded view for a quotation
  const toggleExpandQuotation = (quotationId: string) => {
    setExpandedQuotation(prev => prev === quotationId ? null : quotationId);
  };

  const extractCityName = (address: string): string => {
    if (!address) return "Ubicación no disponible";
    
    if (address.startsWith('Lat:')) {
      return 'Ubicación personalizada';
    }
    
    try {
      const parts = address.split(',');
      
      if (parts.length >= 3) {
        let cityPart = '';
        
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i].trim();
          if (part.length >= 3 && !/\d/.test(part)) {
            cityPart = part;
            break;
          }
        }
        
        if (!cityPart && parts.length >= 2) {
          cityPart = parts[1].trim();
        }
        
        if (!cityPart) {
          cityPart = parts[0].trim();
        }
        
        return cityPart;
      }
      
      return parts[0].trim();
    } catch (error) {
      console.error('Error extracting city name:', error);
      return address;
    }
  };

  // Format currency display with appropriate symbol
  const formatCurrencyDisplay = (amount: number, currencyType: Currency = Currency.USD): string => {
    if (currencyType === Currency.MXN) {
      return formatCurrency(amount, Currency.MXN);
    } else {
      const convertedValue = convertCurrency(amount, Currency.MXN, currencyType);
      return `${getCurrencySymbol(currencyType)}${convertedValue.toLocaleString('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}`;
    }
  };

  // Obtener un color de badge para el factor de riesgo
  const getRiskBadgeColor = (riskFactor?: number): string => {
    if (!riskFactor || riskFactor <= 1.0) return '';
    if (riskFactor < 1.2) return 'bg-green-100 text-green-800 border-green-200';
    if (riskFactor < 1.4) return 'bg-lime-100 text-lime-800 border-lime-200';
    if (riskFactor < 1.6) return 'bg-amber-100 text-amber-800 border-amber-200';
    if (riskFactor < 1.8) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md">
        {error}
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="bg-gray-100 p-6 rounded-lg text-center">
        <VehicleIcon type={VehicleType.TORTON} size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('history.noQuotations')}</h3>
        <p className="text-gray-600">
          {t('history.noQuotationsMsg')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{t('history.title')}</h2>
        <p className="text-gray-600">{t('history.subtitle')}</p>
      </div>
      
      {deleteSuccess && (
        <div className="mx-4 sm:mx-6 mt-4 p-3 bg-green-100 text-green-700 rounded-md text-sm flex items-center">
          <AlertCircle size={16} className="mr-2 flex-shrink-0" />
          {t('history.deleteSuccess')}
        </div>
      )}
      
      {deleteError && (
        <div className="mx-4 sm:mx-6 mt-4 p-3 bg-red-100 text-red-700 rounded-md text-sm flex items-center">
          <AlertCircle size={16} className="mr-2 flex-shrink-0" />
          {deleteError}
        </div>
      )}
      
      <div className="divide-y divide-gray-200">
        {quotations.map((quotation) => {
          const originAddress = quotation.originAddress || quotation.origin;
          const destinationAddress = quotation.destinationAddress || quotation.destination;
          
          const originCity = extractCityName(originAddress);
          const destinationCity = extractCityName(destinationAddress);
          
          // Get the currency for this quotation, default to USD if not specified
          const currency = quotation.selectedCurrency || Currency.USD;
          
          // Check if this quotation is expanded
          const isExpanded = expandedQuotation === quotation.id;
          
          // Check if we have a map image for this quotation (captured or static URL)
          const hasMapImage = !!quotation.mapImageUrl;
          
          return (
            <div key={quotation.id} className="hover:bg-gray-50 transition duration-150">
              {/* Quotation Summary (always visible) */}
              <div 
                className="p-4 sm:p-6 cursor-pointer" 
                onClick={() => toggleExpandQuotation(quotation.id!)}
              >
                <div className="flex flex-col">
                  <div className="mb-3">
                    <div className="flex items-center mb-2">
                      <VehicleIcon 
                        type={quotation.vehicleType} 
                        size={24} 
                        className="text-primary mr-2 flex-shrink-0" 
                      />
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">
                        {translateVehicleType(quotation.vehicleType)} {quotation.trailerType && `${t('history.quotationWith')} ${translateTrailerType(quotation.trailerType)}`}
                      </h3>
                    </div>
                    
                    <div className="flex items-start text-sm text-gray-600 mb-1">
                      <MapPin size={16} className="mr-1 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-1">
                        <span className="font-medium">{originCity}</span> → <span className="font-medium">{destinationCity}</span>
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <span>
                        {quotation.createdAt.toLocaleDateString()} - {quotation.distance.toFixed(2)} km
                      </span>
                    </div>
                    
                    {quotation.profitMargin !== undefined && quotation.profitMargin !== null && (
                      <div className="mt-1 flex items-center">
                        <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">
                          {t('history.profitMargin', { margin: quotation.profitMargin })}
                        </span>
                      </div>
                    )}

                    {/* Mostrar información de tipo de carga y factor de riesgo */}
                    {quotation.cargoType && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        <div className="flex items-center">
                          <Package size={12} className="text-gray-600 mr-1 flex-shrink-0" />
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                            {translateCargoType(quotation.cargoType)}
                          </span>
                        </div>
                        
                        {quotation.riskFactor && quotation.riskFactor > 1.0 && (
                          <div className="flex items-center">
                            <ShieldAlert size={12} className="text-amber-600 mr-1 flex-shrink-0" />
                            <span className={`text-xs px-2 py-0.5 rounded border ${getRiskBadgeColor(quotation.riskFactor)}`}>
                              {t('quotationForm.riskFactor')}: {quotation.riskFactor.toFixed(2)}x
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2">
                    <div className="flex items-center text-lg font-bold text-primary">
                      {formatCurrencyDisplay(quotation.totalCost, currency)}
                      <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center">
                        <DollarSign size={12} className="mr-1 flex-shrink-0" />
                        {currency}
                      </span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPDF(quotation);
                        }}
                        className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-sm shadow-sm hover:shadow-md"
                      >
                        <FileText size={16} className="mr-1 flex-shrink-0" />
                        PDF
                      </button>
                      
                      {hasMapImage && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadMapImage(quotation);
                          }}
                          className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm shadow-sm hover:shadow-md"
                        >
                          <Image size={16} className="mr-1 flex-shrink-0" />
                          Mapa
                        </button>
                      )}
                      
                      {showDeleteConfirm === quotation.id ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfirmDelete(quotation.id!);
                            }}
                            disabled={deleteLoading === quotation.id}
                            className="flex items-center text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded text-sm shadow-sm hover:shadow-md"
                          >
                            {deleteLoading === quotation.id ? (
                              <span className="animate-pulse">{t('history.confirmDelete')}</span>
                            ) : (
                              <>{t('history.confirmDelete')}</>
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelDelete();
                            }}
                            disabled={deleteLoading === quotation.id}
                            className="flex items-center text-gray-600 bg-gray-200 hover:bg-gray-300 px-3 py-1.5 rounded text-sm shadow-sm hover:shadow-md"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(quotation.id!);
                          }}
                          className="flex items-center px-3 py-1.5 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md text-sm shadow-sm hover:shadow-md transition-colors"
                        >
                          <Trash2 size={16} className="mr-1 flex-shrink-0" />
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Expanded Quotation Details */}
              {isExpanded && (
                <div className="px-4 sm:px-6 pb-6 pt-2 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Route and Cost Information */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
                        {t('history.routeDetails')}
                      </h4>
                      
                      <div className="space-y-2 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">{t('history.origin')}</p>
                          <p className="font-medium">{quotation.originAddress || quotation.origin}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">{t('history.destination')}</p>
                          <p className="font-medium">{quotation.destinationAddress || quotation.destination}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-600">{t('history.oneWayDistance')}</p>
                            <p className="font-medium">{quotation.distance.toFixed(2)} km</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-600">{t('history.date')}</p>
                            <p className="font-medium">{quotation.createdAt.toLocaleDateString()}</p>
                          </div>
                        </div>
                        
                        {quotation.isMultiSegmentRoute && quotation.segmentDistances && quotation.segmentDistances.length > 1 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600">{t('history.multiSegmentRoute')}</p>
                            <p className="font-medium">{quotation.segmentDistances.length} {t('history.segments')}</p>
                          </div>
                        )}
                      </div>
                      
                      <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 mt-6">
                        {t('history.totalCost')}
                      </h4>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('history.baseCost')}</span>
                          <span className="font-medium">{formatCurrencyDisplay(quotation.baseCost || 0, currency)}</span>
                        </div>
                        
                        {quotation.profitAmount && (
                          <div className="flex justify-between text-green-600">
                            <span>{t('history.profit')} ({quotation.profitMargin}%):</span>
                            <span className="font-medium">+{formatCurrencyDisplay(quotation.profitAmount, currency)}</span>
                          </div>
                        )}
                        
                        {quotation.riskAmount && quotation.riskFactor && (
                          <div className="flex justify-between text-amber-600">
                            <span>{t('history.riskFactor')} ({(quotation.riskFactor - 1) * 100}%):</span>
                            <span className="font-medium">+{formatCurrencyDisplay(quotation.riskAmount, currency)}</span>
                          </div>
                        )}
                        
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <div className="flex justify-between font-bold text-lg text-primary">
                            <span>{t('history.total')}</span>
                            <span>{formatCurrencyDisplay(quotation.totalCost, currency)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Map Image */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex justify-between">
                        <span>{t('history.routeMap')}</span>
                        {hasMapImage && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadMapImage(quotation);
                            }}
                            className="flex items-center text-sm px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          >
                            <Image size={14} className="mr-1" />
                            {t('history.download')}
                          </button>
                        )}
                      </h4>
                      
                      {/* Map Display */}
                      <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        {hasMapImage ? (
                          <div className="w-full h-full relative">
                            <img 
                              src={quotation.mapImageUrl} 
                              alt="Mapa de ruta" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.log('Map image failed to load, showing placeholder');
                                // If static URL fails, show a placeholder
                                e.currentTarget.src = "https://via.placeholder.com/640x320?text=Mapa+no+disponible";
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-20">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadMapImage(quotation);
                                }}
                                className="px-3 py-2 bg-white rounded-lg shadow-lg text-primary hover:bg-gray-50 transition-colors"
                              >
                                <Image size={16} className="mr-1 inline-block" />
                                {t('history.downloadMap')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <span className="text-sm">{t('history.noMapImage')}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Vehicle & Cargo Information */}
                      <div className="mt-4">
                        <h4 className="font-semibold text-gray-800 mb-2">{t('history.configuration')}</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <p className="text-gray-600">{t('history.vehicle')}</p>
                            <p className="font-medium">{translateVehicleType(quotation.vehicleType)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">{t('history.trailer')}</p>
                            <p className="font-medium">{quotation.trailerType ? translateTrailerType(quotation.trailerType) : t('history.noTrailer')}</p>
                          </div>
                          {quotation.cargoType && (
                            <div className="col-span-2 mt-1">
                              <p className="text-gray-600">{t('history.cargo')}</p>
                              <p className="font-medium">{translateCargoType(quotation.cargoType)}
                                {quotation.riskFactor && quotation.riskFactor > 1.0 && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                    {t('history.risk')} {quotation.riskFactor.toFixed(2)}x
                                  </span>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuotationHistory;